'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Calendar, ChevronLeft, MapPin, CheckCircle } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { RatingBadge } from './store-header';
import type { Store } from '@/api/ahafood/get-store-fn';
import { StoreRatingPreview } from '@/app/[locale]/cua-hang/components/rating/rating-preview';
import { useRatingSheet } from '@/app/[locale]/cua-hang/components/rating/rating-sheet-context';
import { useIsMobile } from '@/lib/is-mobile';

interface StoreDetailSheetProps {
  isOpen: boolean;
  onClose: () => void;
  store: Store;
  onOpenFacebook: (pageId: number) => void;
}
const WEEK_DAY_LABELS: Record<number, string> = {
  0: 'Thứ Hai',
  1: 'Thứ Ba',
  2: 'Thứ Tư',
  3: 'Thứ Năm',
  4: 'Thứ Sáu',
  5: 'Thứ Bảy',
  6: 'Chủ Nhật',
};

// Format minutes since midnight (e.g., 420 -> "07:00")
function formatMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
}
// Removed unused getOperatingStatus, using direct open_hours data

export function StoreDetailSheet({
  isOpen,
  onClose,
  store,
  onOpenFacebook,
}: StoreDetailSheetProps) {
  const isMobile = useIsMobile();
  // Share link copied state
  const { openSheet } = useRatingSheet();
  const [copied, setCopied] = React.useState(false);
  const handleShare = React.useCallback(() => {
    // Copy current page URL
    const url = window.location.href;
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        toast.success('Đã copy link cửa hàng vào bộ nhớ tạm');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // ignore copy errors
      });
  }, []);

  // function getTodayIndex() {
  //   return new Date().getDay();
  // }

  const StoreContent = () => (
    <ScrollArea className="flex flex-col size-full overflow-y-scroll pb-20">
      {/* Header with Store Image */}
      <div className="relative w-full h-64 xl:h-80 overflow-hidden rounded-md flex-1 mb-4">
        <Image
          src={
            store?.image_url
              ? store.image_url
              : `${assetPrefix}/static/images/empty-store.webp`
          }
          alt={store?.name ?? 'Store image'}
          fill
          className="object-cover"
          priority
        />
        <Button
          size="icon"
          variant="secondary"
          className="[&_svg]:size-6 flex-none rounded-full absolute top-4 left-4 z-10 hover:bg-neutral-20 transition-colors"
          onClick={onClose}
          aria-label="Quay lại"
        >
          <ChevronLeft />
        </Button>
      </div>

      {/* Store Name */}
      <div className="p-4 w-full overflow-visible">
        <h1 className="text-xl font-bold text-neutral-90 mb-4 line-clamp-3">
          {store?.name ?? 'Cửa hàng'}
        </h1>

        {store?.rating && store.total_sold ? (
          <Button
            onClick={() => openSheet(store)}
            variant="ghost"
            className="mb-4 p-0"
          >
            <RatingBadge
              rating={store?.rating}
              total_sold={store?.total_sold}
            />
          </Button>
        ) : null}

        {/* Facebook Button */}
        {store.page_id && store.page_id !== 0 ? (
          <div className="w-full justify-center flex gap-4">
            <Button
              onClick={() => onOpenFacebook(store?.page_id ?? 0)}
              variant="outline"
              className="w-full mb-6 py-3 border-blue-50 text-blue-50 hover:text-blue-60 font-bold rounded-xl"
            >
              <img
                src={`${assetPrefix}/static/icons/facebook.svg`}
                alt={store?.name ?? 'Facebook image'}
                className="size-5 object-contain"
              />
              Xem fanpage
            </Button>
            <Button
              size="icon"
              variant="secondary"
              className={cn(
                '[&_svg]:size-6 flex-none rounded-full z-10 ',
                copied ? 'bg-green-10' : 'bg-primary-10'
              )}
              onClick={handleShare}
              aria-label="Chia sẻ"
            >
              {copied ? (
                <CheckCircle className="size-6 text-green-50" />
              ) : (
                <img
                  src={`${assetPrefix}/static/icons/share.svg`}
                  alt={store?.name ?? 'Share image'}
                  className="size-5 object-contain"
                />
              )}
            </Button>
          </div>
        ) : null}

        <div className="mb-6">
          <StoreRatingPreview mode="detail" store={store} />
          {/* Address Section */}
        </div>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-neutral-90 mb-3">
            Địa chỉ
          </h2>
          <div className="flex items-start gap-3">
            <MapPin className="size-5 text-neutral-40 mt-0.5 shrink-0" />
            <span className="text-neutral-60 leading-relaxed">
              {store?.nearest_branch?.address ?? store?.address}
            </span>
          </div>
        </div>

        {/* Operating Hours Section */}
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-neutral-90 mb-3">
            Giờ mở cửa
          </h2>
          <div className="space-y-3">
            {store?.open_hours &&
              Object.keys(store.open_hours).map((key) => {
                const dayIndex = Number(key); // 0 -> Thứ Hai, 6 -> Chủ Nhật
                const periods = store.open_hours[dayIndex] || [];
                const validPeriods = periods.filter(
                  (p) => !(p.start_time === 0 && p.end_time === 0)
                );

                return (
                  <div
                    key={dayIndex}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="rounded-full bg-primary-10 p-2">
                        <Calendar className="size-4 text-primary-50" />
                      </div>
                      <span className="text-neutral-80">
                        {WEEK_DAY_LABELS[dayIndex]}
                      </span>
                    </div>
                    {validPeriods.length > 0 ? (
                      <span className="px-3 py-1 rounded-full bg-primary-1 text-neutral-90 text-sm">
                        {validPeriods
                          .map(
                            (p) =>
                              `${formatMinutes(p.start_time)} - ${formatMinutes(p.end_time)}`
                          )
                          .join(', ')}
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-red-10 text-red-50 text-sm">
                        Đóng cửa
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </ScrollArea>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose} modal={false}>
        <SheetContent
          side="bottom"
          className="h-full p-0 mt-0 border-none max-w-lg mx-auto"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <SheetTitle className="hidden">Chi tiết cửa hàng</SheetTitle>
          <StoreContent />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">Chi tiết cửa hàng</DialogTitle>
        </DialogHeader>
        <StoreContent />
      </DialogContent>
    </Dialog>
  );
}
